import { forwardRef, ReactNode } from "react";
import {
  Accordion as ChakraAccordion,
  AccordionItemTriggerProps,
  useAccordionItemContext,
  Box,
} from "@chakra-ui/react";
import { Spacing } from "@shared/theme/types";
import { resolveSpacingProps } from "@shared/utils/styles/spacing-props";

type AccordionTriggerStyleProps = {
  backgroundColor?: string;
  bg?: string;
  border?: string;
  borderRadius?: string;
  borderBottom?: string;
  borderBottomColor?: string;
  width?: string;
  height?: string;
  fontWeight?: string | number;
  color?: string;
  display?: string;
  justifyContent?: string;
  alignItems?: string;
  _hover?: Record<string, unknown>;
  _focus?: Record<string, unknown>;
  _open?: Record<string, unknown>;
} & Spacing;

export type AccordionTriggerProps = {
  children?: ReactNode;
  /**
   * Position of the expand/collapse icon
   * - "right": Icon on the right (default)
   * - "left": Icon on the left
   * - "none": No icon (render your own)
   */
  iconPosition?: "left" | "right" | "none";
  /**
   * Callback fired when the trigger is clicked while collapsed (about to expand)
   */
  onExpand?: () => void;
  /**
   * Custom click handler (in addition to toggle behavior)
   */
  onClick?: () => void;
  /**
   * Override the expanded state (for controlled usage)
   */
  isExpanded?: boolean;
} & AccordionTriggerStyleProps &
  Omit<AccordionItemTriggerProps, "_open" | "children" | "onClick">;

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  (props, ref) => {
    const {
      children,
      iconPosition = "right",
      onExpand,
      onClick,
      isExpanded: isExpandedProp,
      backgroundColor,
      bg,
      // Spacing props
      margin,
      m,
      marginTop,
      mt,
      marginBottom,
      mb,
      marginLeft,
      ml,
      marginRight,
      mr,
      marginX,
      mx,
      marginY,
      my,
      padding,
      p,
      paddingTop,
      pt,
      paddingBottom,
      pb,
      paddingLeft,
      pl,
      paddingRight,
      pr,
      paddingX,
      px,
      paddingY,
      py,
      border,
      borderRadius,
      borderBottom,
      borderBottomColor,
      width,
      height,
      fontWeight,
      color,
      display,
      justifyContent,
      alignItems,
      _hover,
      _focus,
      _open,
      ...rest
    } = props;

    const itemContext = useAccordionItemContext();
    const isExpanded = isExpandedProp ?? itemContext?.open ?? false;

    const handleClick = () => {
      if (!isExpanded && onExpand) {
        onExpand();
      }
      if (onClick) {
        onClick();
      }
    };

    const resolvedBg = bg ?? backgroundColor;

    const spacingProps = resolveSpacingProps({
      margin,
      m,
      marginTop,
      mt,
      marginBottom,
      mb,
      marginLeft,
      ml,
      marginRight,
      mr,
      marginX,
      mx,
      marginY,
      my,
      padding,
      p,
      paddingTop,
      pt,
      paddingBottom,
      pb,
      paddingLeft,
      pl,
      paddingRight,
      pr,
      paddingX,
      px,
      paddingY,
      py,
    });

    // Apply default padding if none provided
    const hasSpacingProps = Object.keys(spacingProps).length > 0;
    const finalSpacingProps = hasSpacingProps
      ? spacingProps
      : { p: "8px 16px 8px 16px" };

    // Adjust border-radius when expanded (flatten bottom corners)
    const openBorderRadius = borderRadius
      ? borderRadius.replace(/6px/g, "6px 6px 0px 0px")
      : undefined;

    const openStyles = {
      ...(openBorderRadius ? { borderRadius: openBorderRadius } : {}),
      ..._open,
    };

    const triggerProps = {
      ref,
      onClick: handleClick,
      display: display ?? "flex",
      justifyContent: justifyContent ?? "space-between",
      alignItems: alignItems ?? "center",
      width: width ?? "100%",
      ...(height !== undefined ? { height } : {}),
      ...(fontWeight !== undefined ? { fontWeight } : {}),
      ...(color !== undefined ? { color } : {}),
      ...(resolvedBg !== undefined ? { bg: resolvedBg } : {}),
      ...finalSpacingProps,
      ...(border !== undefined ? { border } : {}),
      ...(borderRadius !== undefined ? { borderRadius } : {}),
      ...(borderBottom !== undefined ? { borderBottom } : {}),
      ...(borderBottomColor !== undefined ? { borderBottomColor } : {}),
      _open: openStyles,
      _hover: { cursor: "pointer", backgroundColor: "gray.100", ..._hover },
      _focus: { boxShadow: "none", ..._focus },
      ...rest,
    } as AccordionItemTriggerProps;

    const indicatorProps = {
      color: "charcoal.light",
      boxSize: "element.xl",
      css: {
        // Override Chakra's default 1.2em sizing
        fontSize: "inherit",
        "& svg": {
          width: "100%",
          height: "100%",
        },
      },
    } as Record<string, unknown>;

    const showIcon = iconPosition !== "none";

    return (
      <ChakraAccordion.ItemTrigger {...triggerProps}>
        {iconPosition === "left" && showIcon && (
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            boxSize="element.xl"
            minWidth="element.xl"
            minHeight="element.xl"
          >
            <ChakraAccordion.ItemIndicator {...indicatorProps} />
          </Box>
        )}
        {children}
        {iconPosition === "right" && showIcon && (
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            boxSize="element.xl"
            minWidth="element.xl"
            minHeight="element.xl"
          >
            <ChakraAccordion.ItemIndicator {...indicatorProps} />
          </Box>
        )}
      </ChakraAccordion.ItemTrigger>
    );
  }
);

AccordionTrigger.displayName = "AccordionTrigger";

export default AccordionTrigger;
